import { Action, createReducer, on } from '@ngrx/store';

import {
  createHiringManagerTotals,
  HiringManagerCandidatesTotals,
} from '@modules/job-page/store/hiring-manager/hiring-manager-candidates-totals';
import {
  jobCandidatesAdvancedLoadSuccess,
  jobCandidatesInReviewLoadSuccess,
  jobCandidatesRejectedLoadSuccess,
  jobHiringManagerCandidatesLoad,
  jobHiringManagerRemoveItemLocallyAdvanced,
  jobHiringManagerRemoveItemLocallyInReview,
  jobHiringManagerRemoveItemLocallyRejected,
} from '@modules/job-page/store/hiring-manager/hiring-manager.actions';
import { Candidate } from '@shared/models';
import { Pagination } from '@shared/pagination';

import { jobCandidatesLoadFail } from '../job.actions';

export interface HiringManagerCandidatesState {
  inReviewCandidates: Pagination<{ id: number } & Partial<Candidate>>;
  advancedCandidates: Pagination<{ id: number } & Partial<Candidate>>;
  rejectedCandidates: Pagination<{ id: number } & Partial<Candidate>>;
  totals: HiringManagerCandidatesTotals;
  loaded: boolean;
}

export const initialState: HiringManagerCandidatesState = {
  inReviewCandidates: {},
  advancedCandidates: {},
  rejectedCandidates: {},
  totals: createHiringManagerTotals(),
  loaded: false,
};

const reducer = createReducer(
  initialState,
  on(jobHiringManagerCandidatesLoad, (state) => ({
    ...state,
    loaded: false,
  })),
  on(jobCandidatesLoadFail, (state) => ({
    ...state,
    loaded: true,
  })),
  on(jobCandidatesInReviewLoadSuccess, (state, { payload }) => ({
    ...state,
    inReviewCandidates: payload.candidates,
    totals: createHiringManagerTotals(payload),
    loaded: true,
  })),
  on(jobCandidatesAdvancedLoadSuccess, (state, { payload }) => ({
    ...state,
    advancedCandidates: payload.candidates,
    totals: createHiringManagerTotals(payload),
    loaded: true,
  })),
  on(jobCandidatesRejectedLoadSuccess, (state, { payload }) => ({
    ...state,
    rejectedCandidates: payload.candidates,
    totals: createHiringManagerTotals(payload),
    loaded: true,
  })),
  on(jobHiringManagerRemoveItemLocallyInReview, (state, { candidateId }) => {
    const copyInReviewCandidates = removeSelectedCandidateFromContent(state.inReviewCandidates, candidateId);
    return {
      ...state,
      inReviewCandidates: copyInReviewCandidates,
    };
  }),
  on(jobHiringManagerRemoveItemLocallyAdvanced, (state, { candidateId }) => {
    const copyAdvancedCandidates = removeSelectedCandidateFromContent(state.advancedCandidates, candidateId);
    return {
      ...state,
      advancedCandidates: copyAdvancedCandidates,
    };
  }),
  on(jobHiringManagerRemoveItemLocallyRejected, (state, { candidateId }) => {
    const copyRejectedCandidates = removeSelectedCandidateFromContent(state.rejectedCandidates, candidateId);
    return {
      ...state,
      rejectedCandidates: copyRejectedCandidates,
    };
  }),
);

export function hiringManagerCandidatesReducer(state: HiringManagerCandidatesState | undefined, action: Action) {
  return reducer(state, action);
}

function removeSelectedCandidateFromContent(candidatesPagination: Pagination<{ id: number }>, candidateId: number) {
  const copyCandidatePagination = { ...candidatesPagination };
  let updatedInReviewCandidatesContent;
  if (copyCandidatePagination && copyCandidatePagination.content) {
    updatedInReviewCandidatesContent = copyCandidatePagination.content.filter((candidate) => candidate.id !== candidateId);
    copyCandidatePagination.content = updatedInReviewCandidatesContent;
  }
  return copyCandidatePagination;
}
